import React, { useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogTitle, DialogTrigger, GriffelStyle, makeStyles, tokens, PortalMountNodeProvider } from "@fluentui/react-components";
import { PcpDialogSurface } from "../BaseComponents";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { LocalDeviceSettings } from "./LocalDeviceSettings";
import { useHandlers, useIsMobileWithSmallHeight } from "../../hooks";
import { useCallAdapter } from "../../provider";
import { AudioAndVideoDeviceSettings } from "./AudioAndVideoDeviceSettings";
import { SelectVideoEffectPane } from "../VideoEffects/SelectVideoEffectPane";
import { Label, mergeStyles } from "@fluentui/react";
import { devicePermissionSelector, usePcpLiveLessonContext } from "../../store";
import { pcpTokens } from "../../styles/themes/v9/pcpTokens";

//taken from @fluentui\**\react-dialog\library\src\contexts\constants.ts
const SURFACE_PADDING = '24px';
//const MEDIA_QUERY_SHORT_SCREEN = '@media screen and (max-height: 359px)';

interface DeviceSettingsProps {
    isOpen: boolean;
    onClose: () => void;
}

type dialogStylesKeys = "dialogSurface" | "dialogTitle" | "closeBtn" | "dialogBody" | "subtitle" | "subContent";
const useStyles = makeStyles({
    dialogSurface: {
        backgroundColor: pcpTokens.pcpColorNeutralBackground1White,    //"#FFFFFF"
        padding: 0,
    },
    dialogTitle: {
        padding: "8px 24px",
        width: "100%",
        textAlign: 'center',
        color: tokens.colorNeutralForegroundDisabled,    //'#B3B6B9'
    },
    closeBtn: {
        margin: "8px",
    },
    dialogBody: {
        gap: 0,
        maxWidth: '90vw',
    },

    subtitle: {
        width: "100%",
        height: '35px',
        backgroundColor: tokens.colorNeutralBackground1,    //'#F0F2F4',
        padding: "8px 16px 8px 16px",
        margin: '8px 0px 20px 0px',
        lineHeight: '22px',
    },

    subContent: {
        padding: "0px 24px",
        marginBottom: '20px',
    }
} as Record<dialogStylesKeys, GriffelStyle>);

export const DeviceSettingsDialog = (props: DeviceSettingsProps): JSX.Element => {

    const { isOpen, onClose } = props;

    const localDeviceSettingsHandlers = useHandlers(LocalDeviceSettings);

    const devicePermissions = usePcpLiveLessonContext(devicePermissionSelector);

    const adapter = useCallAdapter();
    const deviceState = adapter.getState().devices;

    const styles = useStyles();
    const isShortScreen = useIsMobileWithSmallHeight();

    // Callback ref pattern: React calls setMountNode(element) on mount,
    // allowing portals to render inside DialogSurface's stacking context.
    const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

    const overrideStyles = {
        dialogSurface: isShortScreen ? {
            paddingRight: '4px',
            borderRightWidth: '0',
        } : {},
        dialogBody: {
            maxHeight: `calc(100dvh - 2 * ${SURFACE_PADDING})`
        }
    };

    return (
        <Dialog open={isOpen} modalType={'modal'}>
            <PcpDialogSurface className={mergeStyles(styles.dialogSurface)}
                style={overrideStyles.dialogSurface}
            >
                <DialogBody
                    className={styles.dialogBody}
                    style={overrideStyles.dialogBody}
                >
                    <DialogTitle
                        className={mergeStyles(styles.dialogTitle)}

                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                    className={mergeStyles(styles.closeBtn)}
                                    onClick={onClose}
                                />
                            </DialogTrigger>
                        }

                        style={{ marginBottom: 0 }}>Einstellungen</DialogTitle>
                    <DialogContent>
                        {/* ref div is the portal mount target.
                            Portals inside PortalMountNodeProvider render here,
                            inside DialogSurface's stacking context. */}
                        <div ref={setMountNode}>
                            <PortalMountNodeProvider value={mountNode ?? undefined}>

                                <DeviceSettingsSubTitle>Audio und Video</DeviceSettingsSubTitle>
                                <div className={mergeStyles(styles.subContent)}>
                                    <AudioAndVideoDeviceSettings
                                        cameras={deviceState.cameras}
                                        microphones={deviceState.microphones}
                                        speakers={deviceState.speakers}
                                        microphonePermissionGranted={devicePermissions.audio}
                                        cameraPermissionGranted={devicePermissions.video}

                                        onSelectCamera={localDeviceSettingsHandlers.onSelectCamera}
                                        onSelectMicrophone={localDeviceSettingsHandlers.onSelectMicrophone}
                                        onSelectSpeaker={localDeviceSettingsHandlers.onSelectSpeaker}

                                        selectedCamera={deviceState.selectedCamera}
                                        selectedMicrophone={deviceState.selectedMicrophone}
                                        selectedSpeaker={deviceState.selectedSpeaker}

                                        renderTitle={<></>}
                                    />
                                </div>

                                <DeviceSettingsSubTitle>Effekte</DeviceSettingsSubTitle>
                                <div className={mergeStyles(styles.subContent)}>
                                    <SelectVideoEffectPane renderTitle={<></>} />
                                </div>

                            </PortalMountNodeProvider>
                        </div>
                    </DialogContent>
                </DialogBody>
            </PcpDialogSurface>
        </Dialog>
    );
};




const DeviceSettingsSubTitle = (props: { children: React.ReactNode }): JSX.Element => {
    const styles = useStyles();

    return (
        <Label className={mergeStyles(styles.subtitle)}>{props.children}</Label>
    );
}
